// Record-owned child sections for the dedicated full project page:
// Big Rocks milestones, Risks, Decisions due and Documents. Reuses the
// existing child endpoints and forms; save errors are shown verbatim beside
// the form and drafts survive failures. Failed reads are never shown as empty.
import { useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import {
  createDecision, createMilestone, createProjectDocument, createRisk,
  updateDecision, updateMilestone, updateProjectDocument, updateRisk,
} from "../../../data/projectsClient.js";
import { fmtDate } from "../../../utils/format.js";
import { sortRisksBySeverity } from "../projectFilters.js";
import {
  ActionButton, ChildForm, DocumentForm, Field, SelectField, isSafeDocumentUrl,
  MILESTONE_STATUS_LABELS, RISK_STATUS_LABELS, STATUS_LABELS,
} from "../ProjectInlineExpanded.jsx";
import { getProjectSourceDisplayName } from "../ProjectSourceLinks.jsx";

export const sx = {
  sec: { fontSize: T.fsXxs5, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid, margin: "2px 0 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  addLink: { fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue, background: "none", border: "none", padding: 0, cursor: "pointer", textTransform: "none", letterSpacing: 0 },
  err: { color: T.red, background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 8, padding: "8px 10px", fontSize: T.fsXs, marginTop: 8 },
  muted: { color: T.mid, fontSize: T.fsSm, fontStyle: "italic", padding: "6px 0" },
  item: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: `1px solid ${T.lightGray}`, fontSize: T.fsSm },
};

function ReadError({ error, onRetry, what, testId }) {
  return (
    <div role="alert" data-testid={testId} style={sx.err}>
      Could not load {what}: {error?.message || String(error)}
      {onRetry && <button type="button" onClick={onRetry} style={{ ...sx.addLink, color: T.red, marginLeft: 8 }}>Retry</button>}
    </div>
  );
}

function SaveError({ error, testId }) {
  return error ? <div role="alert" data-testid={testId} style={sx.err}>{error}</div> : null;
}

// Shared edit-state machine: one draft at a time, keep draft on failure.
// onEditorState({ editing, busy }) reports this section to the record owner
// (drawer guards). Held in a ref so parent re-renders cannot loop.
function useChildEditor({ create, update, onSaved, validate, onEditorState }) {
  const [editing, setEditing] = useState(null); // id | "new" | null
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const stateCb = useRef(onEditorState);
  stateCb.current = onEditorState;
  useEffect(() => { stateCb.current?.({ editing: editing != null, busy: saving }); }, [editing, saving]);
  useEffect(() => () => stateCb.current?.({ editing: false, busy: false }), []);
  const start = (id, initial) => { setEditing(id); setDraft(initial); setError(null); };
  const cancel = () => { if (saving) return; setEditing(null); setDraft(null); setError(null); };
  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const save = async () => {
    const problem = validate(draft);
    if (problem) { setError(problem); return; }
    setSaving(true); setError(null);
    try {
      const result = editing === "new" ? await create(draft) : await update(editing, draft);
      onSaved(result, editing === "new");
      setEditing(null); setDraft(null);
    } catch (err) {
      setError(err?.message || "Save failed.");
    } finally { setSaving(false); }
  };
  return { editing, draft, saving, error, start, cancel, set, save, setDraft };
}

const dayOnly = (v) => (typeof v === "string" ? v.match(/^\d{4}-\d{2}-\d{2}(?=$|T)/)?.[0] ?? "" : "");

// Only the fields the child endpoints accept; never echo ids/timestamps back.
export function pickRisk(r) {
  return { title: r.title ?? "", description: r.description ?? "", severity: r.severity ?? "med", mitigation: r.mitigation ?? "", owner: r.owner ?? "", status: r.status ?? "open" };
}
export function pickDecision(d) {
  return { title: d.title ?? "", description: d.description ?? "", owner: d.owner ?? "", neededByDate: dayOnly(d.neededByDate), impactIfDelayed: d.impactIfDelayed ?? "", status: d.status ?? "open" };
}

// ─── Milestones (Big Rocks) ──────────────────────────────────────────────────
export function MilestonesSection({ projectId, state, canEdit, onChanged, onRetry, onEditorState }) {
  const ed = useChildEditor({
    onEditorState,
    create: (d) => createMilestone(projectId, d),
    update: (id, d) => updateMilestone(projectId, id, d),
    validate: (d) => (!d?.name?.trim() ? "Name is required." : null),
    onSaved: (result, isNew) => onChanged((prev) => isNew ? [...prev, result] : prev.map((m) => m.id === result.id ? result : m)),
  });
  const items = useMemo(() => [...(state.items || [])].sort((a, b) => String(a.dueDate || "9999-12-31").localeCompare(String(b.dueDate || "9999-12-31"))), [state.items]);
  const begin = (m) => ed.start(m ? m.id : "new", m
    ? { name: m.name ?? "", dueDate: dayOnly(m.dueDate), status: m.status ?? "on_track", notes: m.notes ?? "" }
    : { name: "", dueDate: "", status: "on_track", notes: "" });
  const form = ed.draft && (
    <div data-testid="full-milestone-form" style={{ background: T.blueSoft, borderRadius: 8, padding: 10, margin: "8px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", gap: 8 }}>
        <Field label="Name" value={ed.draft.name} onChange={(v) => ed.set("name", v)} />
        <Field label="Due date" type="date" value={ed.draft.dueDate} onChange={(v) => ed.set("dueDate", v)} />
        <SelectField label="Status" value={ed.draft.status} onChange={(v) => ed.set("status", v)} options={Object.entries(MILESTONE_STATUS_LABELS)} />
      </div>
      <Field label="Notes" value={ed.draft.notes} onChange={(v) => ed.set("notes", v)} />
      <div style={{ display: "flex", gap: 6 }}>
        <ActionButton testId="full-milestone-save" onClick={ed.save} disabled={ed.saving}>{ed.saving ? "Saving..." : "Save"}</ActionButton>
        <ActionButton onClick={ed.cancel} secondary disabled={ed.saving}>Cancel</ActionButton>
      </div>
      <SaveError error={ed.error} testId="full-milestone-error" />
    </div>
  );
  return (
    <div data-testid="full-milestones" style={{ marginBottom: 18 }}>
      <div style={sx.sec}>
        <span>Milestones (big rocks)</span>
        {canEdit && state.status === "ok" && <button type="button" style={sx.addLink} onClick={() => begin(null)} data-testid="full-add-milestone">+ Add milestone</button>}
      </div>
      {state.status === "loading" && <div style={sx.muted}>Loading milestones...</div>}
      {state.status === "error" && <ReadError error={state.error} onRetry={onRetry} what="milestones" testId="full-milestones-error" />}
      {state.status === "ok" && items.length === 0 && ed.editing !== "new" && <div style={sx.muted}>No milestones recorded.</div>}
      {state.status === "ok" && items.map((m) => {
        const done = m.status === "complete";
        return (
          <div key={m.id}>
            <div className="full-mrow" data-testid={`full-milestone-${m.id}`} style={{ ...sx.item, alignItems: "center", borderBottom: `1px solid ${T.lightGray}` }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span
                  aria-label={done ? "Complete" : MILESTONE_STATUS_LABELS[m.status] || "Pending"}
                  role="img"
                  data-testid={`full-milestone-mark-${m.id}`}
                  data-state={done ? "done" : "pending"}
                  style={{ width: 17, height: 17, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.white, fontSize: T.fsXxs, background: done ? T.green : "transparent", border: `1.5px solid ${done ? T.green : T.borderDark}` }}
                >
                  {done && <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true"><path d="M1.5 5.2l2.3 2.3 4.7-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </span>
                <span style={{ color: T.dark, overflowWrap: "anywhere" }}>{m.name}</span>
                {m.status === "at_risk" && <span style={{ fontSize: T.fsXxs5, fontWeight: T.fwBold, color: T.amberDark, background: T.utilAmber, borderRadius: 5, padding: "0 6px" }}>At risk</span>}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                <span style={{ fontSize: T.fsXs, color: T.mid }}>{m.dueDate ? `${done ? "" : "target "}${fmtDate(m.dueDate)}` : "No date"}</span>
                {canEdit && <button type="button" className="full-reveal" style={sx.addLink} onClick={() => begin(m)} data-testid={`full-milestone-edit-${m.id}`} aria-label={`Edit milestone ${m.name}`}>Edit</button>}
              </span>
            </div>
            {ed.editing === m.id && form}
          </div>
        );
      })}
      {ed.editing === "new" && form}
    </div>
  );
}

// ─── Collapsible section shell ───────────────────────────────────────────────
function Collapsible({ title, count, defaultOpen, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = `${testId}-body`;
  return (
    <div data-testid={testId} style={{ border: `1px solid ${T.border}`, borderRadius: 9, marginBottom: 9 }}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 13px", background: T.hoverBg, border: "none", borderRadius: open ? "9px 9px 0 0" : 9, cursor: "pointer", fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, textAlign: "left" }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span aria-hidden="true" style={{ display: "inline-block", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s", color: T.mid, fontSize: T.fsXxs }}>&#9654;</span>
          {title}
        </span>
        <span data-testid={`${testId}-count`} style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid, background: T.lightGray, borderRadius: 999, padding: "1px 9px" }}>{count}</span>
      </button>
      {open && <div id={bodyId} style={{ padding: "10px 13px" }}>{children}</div>}
    </div>
  );
}

const SEVERITY = { high: ["HIGH", T.red, T.redSoft], med: ["MED", T.amberDark, T.utilAmber], low: ["LOW", T.green, T.greenSoft] };

export function RisksSection({ projectId, state, canEdit, onChanged, onRetry, onEditorState }) {
  const ed = useChildEditor({
    onEditorState,
    create: (d) => createRisk(projectId, { ...d, title: d.title.trim() }),
    update: (id, d) => updateRisk(projectId, id, { ...d, title: d.title.trim() }),
    validate: (d) => (!d?.title?.trim() ? "Title is required." : null),
    onSaved: (result, isNew) => onChanged((prev) => isNew ? [...prev, result] : prev.map((r) => r.id === result.id ? result : r)),
  });
  const items = useMemo(() => sortRisksBySeverity(state.items || []), [state.items]);
  const open = items.filter((r) => r.status === "open" || !r.status);
  const begin = (r) => ed.start(r ? r.id : "new", r ? pickRisk(r) : { title: "", description: "", severity: "med", mitigation: "", owner: "", status: "open" });
  const form = ed.draft && (
    <>
      <ChildForm kind="risk" value={ed.draft} onChange={ed.set} onSave={ed.save} onCancel={ed.cancel} saving={ed.saving} />
      <SaveError error={ed.error} testId="full-risk-error" />
    </>
  );
  const count = state.status === "ok" ? open.length : state.status === "error" ? "!" : "...";
  return (
    <Collapsible title="Open risks" count={count} defaultOpen testId="full-risks">
      {state.status === "loading" && <div style={sx.muted}>Loading risks...</div>}
      {state.status === "error" && <ReadError error={state.error} onRetry={onRetry} what="risks" testId="full-risks-error" />}
      {state.status === "ok" && items.length === 0 && <div style={sx.muted}>No risks recorded.</div>}
      {state.status === "ok" && items.map((r) => {
        const [lab, fg, bg] = SEVERITY[r.severity] || ["-", T.mid, T.lightGray];
        return (
          <div key={r.id}>
            <div className="full-mrow" data-testid={`full-risk-${r.id}`} style={sx.item}>
              <span style={{ minWidth: 0, color: r.status && r.status !== "open" ? T.mid : T.dark }}>
                <span data-testid={`full-risk-severity-${r.id}`} style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, borderRadius: 5, padding: "1px 7px", marginRight: 8, color: fg, background: bg }}>{lab}</span>
                {r.title}
                {r.status && r.status !== "open" && <span style={{ marginLeft: 6, fontSize: T.fsXxs5, color: T.mid }}>({RISK_STATUS_LABELS[r.status] || r.status})</span>}
                {r.mitigation && <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>Mitigation: {r.mitigation}</div>}
              </span>
              {canEdit && <button type="button" className="full-reveal" style={sx.addLink} onClick={() => begin(r)} data-testid={`full-risk-edit-${r.id}`} aria-label={`Edit risk ${r.title}`}>Edit</button>}
            </div>
            {ed.editing === r.id && form}
          </div>
        );
      })}
      {ed.editing === "new" && form}
      {canEdit && state.status === "ok" && ed.editing !== "new" && <div style={{ marginTop: 8 }}><button type="button" style={sx.addLink} onClick={() => begin(null)} data-testid="full-add-risk">+ Add risk</button></div>}
    </Collapsible>
  );
}

export function DecisionsSection({ projectId, state, canEdit, onChanged, onRetry, onEditorState }) {
  const ed = useChildEditor({
    onEditorState,
    create: (d) => createDecision(projectId, { ...d, title: d.title.trim() }),
    update: (id, d) => updateDecision(projectId, id, { ...d, title: d.title.trim() }),
    validate: (d) => (!d?.title?.trim() ? "Title is required." : null),
    onSaved: (result, isNew) => onChanged((prev) => isNew ? [...prev, result] : prev.map((r) => r.id === result.id ? result : r)),
  });
  const items = useMemo(() => [...(state.items || [])].sort((a, b) =>
    (a.status === "open" ? 0 : 1) - (b.status === "open" ? 0 : 1)
    || String(a.neededByDate || "9999").localeCompare(String(b.neededByDate || "9999"))), [state.items]);
  const open = items.filter((d) => d.status === "open");
  const begin = (d) => ed.start(d ? d.id : "new", d ? pickDecision(d) : { title: "", description: "", owner: "", neededByDate: "", impactIfDelayed: "", status: "open" });
  const form = ed.draft && (
    <>
      <ChildForm kind="decision" value={ed.draft} onChange={ed.set} onSave={ed.save} onCancel={ed.cancel} saving={ed.saving} />
      <SaveError error={ed.error} testId="full-decision-error" />
    </>
  );
  const count = state.status === "ok" ? open.length : state.status === "error" ? "!" : "...";
  return (
    <Collapsible title="Decisions due" count={count} defaultOpen testId="full-decisions">
      {state.status === "loading" && <div style={sx.muted}>Loading decisions...</div>}
      {state.status === "error" && <ReadError error={state.error} onRetry={onRetry} what="decisions" testId="full-decisions-error" />}
      {state.status === "ok" && items.length === 0 && <div style={sx.muted}>No decisions recorded.</div>}
      {state.status === "ok" && items.map((d) => (
        <div key={d.id}>
          <div className="full-mrow" data-testid={`full-decision-${d.id}`} style={sx.item}>
            <span style={{ minWidth: 0, color: d.status === "open" ? T.dark : T.mid }}>
              {d.title}
              {d.status !== "open" && <span style={{ marginLeft: 6, fontSize: T.fsXxs5 }}>({STATUS_LABELS[d.status] || d.status})</span>}
              {d.owner && <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>{d.owner}</div>}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <span data-testid={`full-decision-due-${d.id}`} style={{ fontSize: T.fsXs, fontWeight: T.fwBold, whiteSpace: "nowrap", color: d.neededByDate ? T.amberDark : T.mid }}>
                {d.neededByDate ? `by ${fmtDate(d.neededByDate)}` : "No due date"}
              </span>
              {canEdit && <button type="button" className="full-reveal" style={sx.addLink} onClick={() => begin(d)} data-testid={`full-decision-edit-${d.id}`} aria-label={`Edit decision ${d.title}`}>Edit</button>}
            </span>
          </div>
          {ed.editing === d.id && form}
        </div>
      ))}
      {ed.editing === "new" && form}
      {canEdit && state.status === "ok" && ed.editing !== "new" && <div style={{ marginTop: 8 }}><button type="button" style={sx.addLink} onClick={() => begin(null)} data-testid="full-add-decision">+ Log decision</button></div>}
    </Collapsible>
  );
}

export function documentLabel(doc) {
  const name = getProjectSourceDisplayName({ sourceType: "other", label: doc.source }) || doc.source || "Document";
  return doc.pageRef ? `${name} - ${doc.pageRef}` : name;
}

export function DocumentsSection({ projectId, state, canEdit, onChanged, onRetry, onEditorState }) {
  const ed = useChildEditor({
    onEditorState,
    create: (d) => createProjectDocument(projectId, { ...d, url: d.url.trim(), pageRef: d.pageRef?.trim() || null }),
    update: (id, d) => updateProjectDocument(projectId, id, { ...d, url: d.url.trim(), pageRef: d.pageRef?.trim() || null }),
    validate: (d) => (!isSafeDocumentUrl(d?.url) ? "Enter a valid http(s) URL." : null),
    onSaved: (result, isNew) => onChanged((prev) => isNew ? [...prev, result] : prev.map((r) => r.id === result.id ? result : r)),
  });
  const items = state.items || [];
  const begin = (d) => ed.start(d ? d.id : "new", d ? { source: d.source || "Other", url: d.url || "", pageRef: d.pageRef || "" } : { source: "JLL Weekly", url: "", pageRef: "" });
  const form = ed.draft && (
    <>
      <DocumentForm value={ed.draft} onChange={ed.set} onSave={ed.save} onCancel={ed.cancel} saving={ed.saving} />
      <SaveError error={ed.error} testId="full-document-error" />
    </>
  );
  const count = state.status === "ok" ? items.length : state.status === "error" ? "!" : "...";
  return (
    <Collapsible title="Documents" count={count} defaultOpen={false} testId="full-documents">
      {state.status === "loading" && <div style={sx.muted}>Loading documents...</div>}
      {state.status === "error" && <ReadError error={state.error} onRetry={onRetry} what="documents" testId="full-documents-error" />}
      {state.status === "ok" && items.length === 0 && <div style={sx.muted}>No documents linked.</div>}
      {state.status === "ok" && items.map((d) => {
        const safe = isSafeDocumentUrl(d.url);
        const label = documentLabel(d);
        return (
          <div key={d.id}>
            <div className="full-mrow" data-testid={`full-document-${d.id}`} style={sx.item}>
              {safe
                ? <a href={d.url.trim()} target="_blank" rel="noopener noreferrer" style={{ color: T.blue, fontWeight: T.fwBold, textDecoration: "none", overflowWrap: "anywhere" }}>{label}</a>
                : <span style={{ color: T.dark, fontWeight: T.fwBold }}>{label} <span style={{ color: T.mid, fontWeight: T.fwSemibold, fontSize: T.fsXs }}>(link unavailable)</span></span>}
              <span style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                {canEdit && <button type="button" className="full-reveal" style={sx.addLink} onClick={() => begin(d)} data-testid={`full-document-edit-${d.id}`} aria-label={`Edit document ${label}`}>Edit</button>}
                {safe && <a href={d.url.trim()} target="_blank" rel="noopener noreferrer" style={{ ...sx.addLink, textDecoration: "none" }} data-testid={`full-document-open-${d.id}`} aria-label={`Open ${label}`}>Open</a>}
              </span>
            </div>
            {ed.editing === d.id && form}
          </div>
        );
      })}
      {ed.editing === "new" && form}
      {canEdit && state.status === "ok" && ed.editing !== "new" && <div style={{ marginTop: 8 }}><button type="button" style={sx.addLink} onClick={() => begin(null)} data-testid="full-add-document">+ Add document</button></div>}
    </Collapsible>
  );
}
